import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Card,
    CardContent,
    Typography,
    Box,
    Grid,
    Button,
    Switch,
    FormControlLabel,
    TextField,
    Alert,
    Divider,
    Slider,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Chip,
    IconButton,
    Tabs,
    Tab,
    Tooltip,
} from "@mui/material";
import {
    CloudUpload,
    Save,
    Delete,
    TextFields,
    FormatAlignLeft,
    FormatAlignCenter,
    FormatAlignRight,
    Visibility,
    VisibilityOff,
    FontDownload,
    BorderColor,
    ArrowBack,
} from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import { styled } from "@mui/material/styles";
import { MuiColorInput } from "mui-color-input";
import LogoUploadSection from "./LogoUploadSection";
import MainWayfinderMedia from "./MainWayfinderMedia";
import MainTextPage from "./MainTextPage";

// Styled Components
const StyledDropzone = styled("div")(({ theme }) => ({
    border: "2px dashed #ccc",
    borderRadius: "8px",
    padding: "40px",
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: "#f8f9fa",
    transition: "all 0.3s",
    "&:hover": {
        backgroundColor: "#e9ecef",
        borderColor: theme.palette.primary.main,
    },
}));

const StyledCard = ({ children, title, icon: Icon }) => (
    <Card sx={{ height: "100%", boxShadow: 2, borderRadius: 2 }}>
        <CardContent
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
            {title && (
                <>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                        {Icon && <Icon sx={{ mr: 1, color: "primary.main" }} />}
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {title}
                        </Typography>
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                </>
            )}
            {children}
        </CardContent>
    </Card>
);

// Text Styling Component
const TextStylingSection = ({ title, data, onChange }) => {
    const handleTextChange = (field, value) => {
        onChange({
            ...data,
            [field]: value,
        });
    };

    const handleNumberChange = (field, value) => {
        const numValue = parseInt(value) || 0;
        handleTextChange(field, numValue);
    };

    const isLeftPositionDisabled =
        data.textAlign === "center" || data.textAlign === "right";

    return (
        <StyledCard title={title} icon={TextFields}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Text Content"
                        value={data.text || ""}
                        onChange={(e) => handleTextChange("text", e.target.value)}
                        multiline
                        rows={2}
                        placeholder={`Enter ${title.toLowerCase()} text...`}
                    />
                </Grid>

                <Grid item xs={6}>
                    <MuiColorInput
                        label="Text Color"
                        format="hex"
                        value={data.color}
                        onChange={(value) => handleTextChange("color", value)}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FontDownload fontSize="small" />
                        <TextField
                            fullWidth
                            label="Font Size"
                            type="number"
                            value={data.fontSize || 16}
                            onChange={(e) => handleNumberChange("fontSize", e.target.value)}
                        />
                    </Box>
                </Grid>

                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel>Font Weight</InputLabel>
                        <Select
                            value={data.fontWeight || 400}
                            onChange={(e) => handleTextChange("fontWeight", e.target.value)}
                            label="Font Weight"
                        >
                            <MenuItem value={100}>Thin (100)</MenuItem>
                            <MenuItem value={300}>Light (300)</MenuItem>
                            <MenuItem value={400}>Normal (400)</MenuItem>
                            <MenuItem value={500}>Medium (500)</MenuItem>
                            <MenuItem value={600}>Semi Bold (600)</MenuItem>
                            <MenuItem value={700}>Bold (700)</MenuItem>
                            <MenuItem value={900}>Black (900)</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel>Text Align</InputLabel>
                        <Select
                            value={data.textAlign || "left"}
                            onChange={(e) => handleTextChange("textAlign", e.target.value)}
                            label="Text Align"
                        >
                            <MenuItem value="left">
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <FormatAlignLeft sx={{ mr: 1 }} /> Left
                                </Box>
                            </MenuItem>
                            <MenuItem value="center">
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <FormatAlignCenter sx={{ mr: 1 }} /> Center
                                </Box>
                            </MenuItem>
                            <MenuItem value="right">
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <FormatAlignRight sx={{ mr: 1 }} /> Right
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        label="Top Position"
                        type="number"
                        value={data.top || 10}
                        onChange={(e) => handleNumberChange("top", e.target.value)}
                        InputProps={{ endAdornment: "%" }}
                    />
                </Grid>

                <Grid item xs={6}>
                    <Tooltip
                        title={
                            isLeftPositionDisabled
                                ? "Disabled when text align is center or right"
                                : ""
                        }
                    >
                        <TextField
                            fullWidth
                            label="Left Position"
                            type="number"
                            value={data.left || 10}
                            onChange={(e) => handleNumberChange("left", e.target.value)}
                            InputProps={{ endAdornment: "%" }}
                            disabled={isLeftPositionDisabled}
                            sx={{
                                "& .Mui-disabled": {
                                    backgroundColor: "#f5f5f5",
                                },
                            }}
                        />
                    </Tooltip>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                        Text Shadow
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={3}>
                            <TextField
                                fullWidth
                                label="H-offset"
                                value={data.textShadowH || "2"}
                                onChange={(e) =>
                                    handleTextChange("textShadowH", e.target.value)
                                }
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                fullWidth
                                label="V-offset"
                                value={data.textShadowV || "2"}
                                onChange={(e) =>
                                    handleTextChange("textShadowV", e.target.value)
                                }
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                fullWidth
                                label="Blur"
                                value={data.textShadowBlur || "4"}
                                onChange={(e) =>
                                    handleTextChange("textShadowBlur", e.target.value)
                                }
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <MuiColorInput
                                label="Shadow Color"
                                format="hex"
                                value={data.textShadowColor}
                                onChange={(value) => handleTextChange("textShadowColor", value)}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                        Opacity: {Math.round((data.opacity || 1) * 100)}%
                    </Typography>
                    <Slider
                        value={data.opacity || 1}
                        onChange={(e, value) => handleTextChange("opacity", value)}
                        min={0}
                        max={1}
                        step={0.1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                    />
                </Grid>
            </Grid>
        </StyledCard>
    );
};

// Main Template Component
const WayfinderMainCategoriesTemplate = ({
    title = "Category",
    categoryData,
    activeTab,
    setActiveTab,
    loading,
    message,
    setMessage,
    handleSave,
    handleReset,
    renderLivePreview,
    handleTextStylingChange,
    handleFieldChange,
    handleUploadLogo,
    handleLogoChange,
    handleDeleteLogo,
    getPositionName,
    uploadedUrls,
    setUploadedUrls,
    uploadingStates,
    setUploadingStates,
    mediaTitle,
    loadingMedia,
    fetchInitialData,
    setCategoryData,
    userId,
    handleTextSectionChange,
    handleToggleTextSection,
    saveTextData,
    mainTextSections,
    loadingText,


}) => {
    const navigate = useNavigate();
    const buttonColor = "#000";

    console.log("fetchInitialData:", fetchInitialData);

    return (
        <div className="main-container">
            <div className="pd-ltr-20 xs-pd-20-10  dashboard-container">
                <Box sx={{ mx: "auto" }}>
                    {/* Header */}
                    <div className="page-header">
                        <div className="row">
                            <div className="col-md-6 col-sm-12">
                                <div className="title">
                                    <Typography variant="h4">
                                        Wayfinder {">>"}{" "}
                                        <span style={{ color: buttonColor }}>
                                            {title?.replace(/<br\s*\/?>/gi, "")}
                                        </span>
                                    </Typography>
                                </div>
                            </div>
                            <div className="col-md-6 col-sm-12">
                                <Button
                                    variant="contained"
                                    startIcon={<ArrowBack />}
                                    onClick={() => navigate(-1)}
                                    sx={{
                                        float: "right",
                                        borderColor: buttonColor,
                                        color: "white",
                                        backgroundColor: buttonColor,
                                        "&:hover": {
                                            backgroundColor: buttonColor,
                                            color: "white",
                                        },
                                    }}
                                >
                                    Go Back
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Message Alert */}
                    {message.text && (
                        <Alert
                            severity={message.type}
                            sx={{ mb: 3 }}
                            onClose={() => setMessage({ type: "", text: "" })}
                        >
                            {message.text}
                        </Alert>
                    )}

                    {/* Tabs Navigation */}
                    <Tabs
                        value={activeTab}
                        onChange={(e, newValue) => setActiveTab(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            '& .MuiTab-root': { minWidth: 100 }, // Ensures consistent width
                            '& .Mui-selected': { fontWeight: 'bold' }, // Active tab styling
                        }}
                    >
                        <Tab sx={{
                            '&:hover': {
                                backgroundColor: buttonColor, // Add your desired hover color here
                                color: '#fff',
                                fontWeight: 'bold',
                            },
                        }} label="Page Content" />
                        <Tab sx={{
                            '&:hover': {
                                backgroundColor: buttonColor, // Add your desired hover color here
                                color: '#fff',
                                fontWeight: 'bold',
                            },
                        }} label="Page Text Settings" />
                    </Tabs>


                    {/* Tab Content */}
                    <Box sx={{ mt: 3 }}>
                        {activeTab === 0 && (
                            <>
                                <MainWayfinderMedia
                                    userId={userId}
                                    buttonColor={buttonColor}
                                    title={mediaTitle}
                                    uploadedUrls={uploadedUrls}
                                    setUploadedUrls={setUploadedUrls}
                                    uploadingStates={uploadingStates}
                                    setUploadingStates={setUploadingStates}
                                    handleUpload={handleUploadLogo}
                                    handleDeleteLogo={handleDeleteLogo}
                                    handleSave={handleSave}
                                    loading={loadingMedia}
                                    categoryData={categoryData}
                                    setCategoryData={setCategoryData}
                                    fetchCategoryData={fetchInitialData}
                                    showSaveBtn={true}
                                />
                            </>
                        )}

                        {activeTab === 1 && (
                            <MainTextPage
                                userId={userId}
                                buttonColor={buttonColor}
                                sectionType="category"
                                title="Sub Category Text Settings"
                                sections={mainTextSections}
                                loading={loadingText}
                                onSectionChange={(section, field, value) => handleTextSectionChange("location", section, field, value)}
                                onToggleSection={(section) => handleToggleTextSection("location", section)}
                                onSave={() => saveTextData('location', mainTextSections)}
                            />
                        )}


                    </Box>

                    {/* Action Buttons */}
                    {/* <Card sx={{ mt: 3, borderRadius: 2, backgroundColor: "#f8f9fa" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={loading}
                  startIcon={<Save />}
                  sx={{
                    backgroundColor: buttonColor,
                    "&:hover": { backgroundColor: buttonColor },
                    px: 4,
                    py: 1.5,
                    minWidth: 200,
                  }}
                >
                  {loading ? "Saving..." : "Save All Settings"}
                </Button>

            <Button
                                    variant="outlined"
                                    onClick={handleReset}
                                    disabled={loading}
                                    startIcon={<Delete />}
                                    sx={{
                                        borderColor: '#e74c3c',
                                        color: '#e74c3c',
                                        '&:hover': {
                                            borderColor: '#c0392b',
                                            backgroundColor: 'rgba(231, 76, 60, 0.04)'
                                        },
                                        px: 4,
                                        py: 1.5,
                                        minWidth: 200
                                    }}
                                >
                                    Reset All
                                </Button>  
              </Box>
            </CardContent>
          </Card> */}
                </Box>
            </div>
        </div>
    );
};

export default WayfinderMainCategoriesTemplate;
